import axiosInstance from 'Services/axios';

/**
 * Uploads an image file (File, Blob, or base64) to the communications image upload API.
 * @param {File|Blob|string} imageSource - The image to upload (File/Blob object or base64 string)
 * @param {string} templateId - The UUID of the template this image belongs to
 * @returns {Promise<string>} The URL of the uploaded image
 */
export const uploadCommunicationsImage = async (imageSource, templateId) => {
  try {
    const formData = new FormData();
    
    if (typeof imageSource === 'string' && imageSource.startsWith('data:image')) {
      // Handle base64 string
      const [header, base64Data] = imageSource.split(',');
      const mime = header.match(/:(.*?);/)[1];
      const byteCharacters = atob(base64Data);
      const byteArrays = [];
      
      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
      
      const blob = new Blob(byteArrays, { type: mime });
      formData.append('file', blob);
    } else if (typeof imageSource === 'string' && imageSource.startsWith('blob:')) {
      // Handle blob URL
      const response = await fetch(imageSource);
      const blob = await response.blob();
      formData.append('file', blob);
    } else if (imageSource instanceof File || imageSource instanceof Blob) {
      // Handle actual File or Blob object
      formData.append('file', imageSource);
    } else {
      throw new Error('Unsupported image source type');
    }

    if (!templateId) {
      throw new Error('templateId is required for communications image upload');
    }
    formData.append('template_id', templateId);

    // Use our global upload endpoint
    const response = await axiosInstance.post('/api/v1/communications-images/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Backend returns: { data: { file_url: "..." }, message: "File uploaded successfully." }
    const responseData = response.data?.data || response.data;
    const imageUrl = responseData?.file_url || responseData?.url || responseData?.image_url;
    console.log('[ImageUpload] API Response for upload:', response?.data, '-> Resolved URL:', imageUrl);
    
    if (!imageUrl || typeof imageUrl !== 'string') {
      throw new Error('Invalid response format from image upload API');
    }

    return imageUrl;
  } catch (error) {
    console.error('[ImageUpload] Error uploading image:', error);
    throw error;
  }
};

/**
 * Parses an HTML/MJML string, finds local images, uploads them, and returns the updated string.
 * @param {string} content - HTML or MJML string content
 * @param {string} templateId - The UUID of the template this image belongs to
 * @returns {Promise<string>} The updated content with remote image URLs
 */
export const processTemplateImages = async (content, templateId) => {
  if (!content) return content;
  if (!templateId) {
    console.warn('[ImageUpload] No templateId provided, skipping image upload processing.');
    return content;
  }
  
  // Find all src="..." attributes in the content
  // Regex looks for src="..." or src='...' containing data:image or blob:
  const srcRegex = /src=["']((data:image\/[^;]+;base64,[^"']+)|(blob:[^"']+))["']/gi;
  let match;
  let updatedContent = content;
  
  // Collect all unique matches to avoid uploading the same image multiple times
  const matchesToProcess = new Map();
  
  while ((match = srcRegex.exec(content)) !== null) {
    const fullMatch = match[0];
    const imageSource = match[1];
    
    // Only process exactly base64 or blob URLs
    if (imageSource.startsWith('data:image/') || imageSource.startsWith('blob:')) {
      if (!matchesToProcess.has(imageSource)) {
        matchesToProcess.set(imageSource, []);
      }
      matchesToProcess.get(imageSource).push(fullMatch);
    }
  }

  // If no local images found, just return original content
  if (matchesToProcess.size === 0) {
    console.log('[ImageUpload] No local images found in template content.');
    return content;
  }

  console.log(`[ImageUpload] Found ${matchesToProcess.size} unique local images to upload.`);

  // Handle all uploads in parallel
  const uploadPromises = Array.from(matchesToProcess.keys()).map(async (imageSource) => {
    try {
      const remoteUrl = await uploadCommunicationsImage(imageSource, templateId);
      return { oldSource: imageSource, newSource: remoteUrl, instances: matchesToProcess.get(imageSource) };
    } catch (error) {
      console.error(`Failed to upload image ${imageSource.substring(0, 30)}...`, error);
      // If one fails, we just don't replace it and return null for this specific promise
      return null;
    }
  });

  const results = await Promise.all(uploadPromises);

  // Replace old local sources with new remote URLs
  for (const result of results) {
    if (result && result.newSource) {
      console.log(`[ImageUpload] Replacing local source with remote URL: ${result.newSource}`);
      // Use split/join instead of RegExp for the replacement.
      // A large base64 string (e.g. 500kb+) exceeds the V8 engine maximum 
      // length for a regular expression and throws a SyntaxError.
      // Replace instances with double quotes
      updatedContent = updatedContent.split(`src="${result.oldSource}"`).join(`src="${result.newSource}"`);
      // Replace instances with single quotes
      updatedContent = updatedContent.split(`src='${result.oldSource}'`).join(`src="${result.newSource}"`);
    }
  }

  console.log('[ImageUpload] Completed processing template images.');
  return updatedContent;
};
